/**
 * User-friendly muted color palette for analytics charts
 */
export const analyticsColorPalette = [
    '#5A9BD4', // Muted blue
    '#70C285', // Muted green
    '#D47A6B', // Muted red
    '#E6A756', // Muted orange
    '#A784C7', // Muted purple
    '#5AB5A5', // Muted turquoise
    '#6B7D8A', // Muted blue-gray
    '#D4955A', // Muted carrot orange
    '#9AAB9D', // Muted gray
    '#5A9B8A', // Muted dark turquoise
    '#E6C555', // Muted yellow
    '#D16B8C', // Muted pink
    '#9A6FB8', // Muted dark purple
    '#6BA570', // Muted dark green
    '#6B96C7', // Muted dark blue
]

/**
 * Standard label styling for analytics charts
 * Consistent font size and weight across all chart types
 */
export const chartLabelStyle = {
    fontSize: '12px',
    fontWeight: '500' as const,
    opacity: 0.9
}

/**
 * Simple hash function to convert a string to a numeric hash
 * @param str The string to hash
 * @returns A numeric hash value
 */
function hashString(str: string): number {
    let hash = 0
    if (str.length === 0) return hash
    
    for (let i = 0; i < str.length; i += 1) {
        const char = str.charCodeAt(i)
        // Simple hash algorithm (djb2) - avoid bitwise XOR for ESLint
        hash = ((hash * 33) + char) % 2147483647
    }
    
    return Math.abs(hash)
}

/**
 * Get a deterministic color from the analytics palette based on a string identifier
 * This ensures the same identifier always gets the same color while appearing random
 * @param identifier The string to base color selection on (e.g., chart title, data key)
 * @param offset Optional offset to get different colors for related items
 * @returns A color from the analytics color palette
 */
export function getAnalyticsColor(identifier = 'default', offset = 0): string {
    const finalIdentifier = identifier || 'default'
    
    const hash = hashString(finalIdentifier.toLowerCase())
    const index = (hash + offset) % analyticsColorPalette.length
    
    return analyticsColorPalette[index]
}

/**
 * Get multiple deterministic colors for a chart with multiple series
 * @param identifier Base identifier for the chart
 * @param count Number of colors needed
 * @returns Array of colors from the analytics palette
 */
export function getAnalyticsColors(identifier = 'default', count = 1): string[] {
    const colors: string[] = []
    
    for (let i = 0; i < count; i += 1) {
        colors.push(getAnalyticsColor(identifier, i))
    }
    
    return colors
}

/**
 * Darken a hex color by a specified percentage
 * @param hex The hex color to darken (e.g., '#3498db')
 * @param percent The percentage to darken (0-100)
 * @returns The darkened hex color
 */
export function darkenColor(hex: string, percent: number = 20): string {
    // Remove the hash symbol if present
    const color = hex.replace('#', '')
    
    // Parse RGB values
    const r = parseInt(color.substr(0, 2), 16)
    const g = parseInt(color.substr(2, 2), 16)
    const b = parseInt(color.substr(4, 2), 16)
    
    // Calculate darkened values
    const darkenedR = Math.round(r * (1 - percent / 100))
    const darkenedG = Math.round(g * (1 - percent / 100))
    const darkenedB = Math.round(b * (1 - percent / 100))
    
    // Convert back to hex
    const toHex = (value: number) => value.toString(16).padStart(2, '0')
    
    return `#${toHex(darkenedR)}${toHex(darkenedG)}${toHex(darkenedB)}`
}

/**
 * Detects if a field name indicates it contains currency values
 */
function isCurrencyField(fieldName: string): boolean {
    if (!fieldName) return false
    const lowerName = fieldName.toLowerCase()
    return lowerName.includes('cost') || lowerName.includes('price') || lowerName.includes('spend') || 
           lowerName.includes('revenue') || lowerName.includes('dollar') || lowerName.includes('amount')
}

/**
 * Format currency values for display
 */
function formatCurrency(value: number, compact: boolean = false): string {
    if (value === 0) return '$0'
    
    // For very small values (< 1), show full precision
    if (Math.abs(value) < 1) {
        return `$${value.toFixed(3).replace(/\.?0+$/, '')}`
    }
    
    // For compact display (axis labels)
    if (compact) {
        if (value >= 1000000) {
            return `$${(value / 1000000).toFixed(1)}M`
        } else if (value >= 1000) {
            return `$${(value / 1000).toFixed(1)}K`
        }
    }
    
    // Standard currency formatting
    if (value >= 100) {
        return `$${Math.round(value).toLocaleString()}`
    } else {
        return `$${value.toFixed(2)}`
    }
}

/**
 * Format Y-axis tick labels with proper handling for currency, duration, and other data types
 * @param value The tick value
 * @param maxCharsPerLine Maximum characters per line before wrapping
 * @param dataKey Optional data key to detect field type
 * @returns Formatted label with potential line breaks
 */
export function formatYAxisLabelWithWrap(value: string | number, maxCharsPerLine: number = 20, dataKey?: string): string {
    const stringValue = String(value)
    
    // For numeric values, use appropriate formatting based on field type
    if (typeof value === 'number') {
        // Check if this appears to be a currency field
        if (dataKey && isCurrencyField(dataKey)) {
            return formatCurrency(value, true)
        }
        
        // Standard numeric formatting for large numbers
        if (value >= 1000000) {
            return (value / 1000000).toFixed(1) + 'M'
        } else if (value >= 1000) {
            return (value / 1000).toFixed(1) + 'K'
        }
        
        // For small decimal numbers, preserve precision
        if (value < 1 && value > 0) {
            return value.toFixed(3).replace(/\.?0+$/, '')
        }
        
        return value.toLocaleString()
    }
    
    // For string values, allow full text without truncation
    // Only wrap if it's extremely long
    if (stringValue.length <= maxCharsPerLine) {
        return stringValue
    }
    
    // Find a good break point (space or hyphen) near the middle
    const breakPoint = stringValue.lastIndexOf(' ', maxCharsPerLine) || 
                      stringValue.lastIndexOf('-', maxCharsPerLine) ||
                      maxCharsPerLine
    
    if (breakPoint > 0 && breakPoint < stringValue.length) {
        return stringValue.substring(0, breakPoint) + '\n' + 
               stringValue.substring(breakPoint + 1)
    }
    
    return stringValue
}

/**
 * Calculate optimal Y-axis width based on data
 * @param data Chart data array
 * @param labelKey The key used for labels
 * @param baseWidth Base width in pixels
 * @returns Calculated width for Y-axis
 */
export function calculateYAxisWidth(data: any[], labelKey: string, baseWidth: number = 60): number {
    if (!data || data.length === 0) return baseWidth
    
    // Find the longest label
    const maxLabelLength = data.reduce((max, item) => {
        const label = String(item[labelKey] || '')
        return Math.max(max, label.length)
    }, 0)
    
    // Calculate width based on character count (roughly 8px per character + 20px padding)
    // Allow for more generous spacing without caps
    const calculatedWidth = Math.max(maxLabelLength * 4 + 20, baseWidth)
    
    return calculatedWidth
}